import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import swal from "sweetalert";

import Modal from "../../../myfin_v2/elements/Modal";
import buttoncss from "../../../myfin_v2/css/button.module.css";
import InputGroup from "../../../myfin_v2/elements/InputGroup";
import { Button } from "../../../myfin_v2/elements/Button";
import commoncss from "../../../myfin_v2/css/common.module.css";
import CommonDatePicker from "../../elements/CommonDatePicker";
import SuccessPopup from "../../elements/SuccessPopup";

import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import { ReactComponent as CalenderIcon } from "../../../myfin_v2/assets/icons/calender.svg";
import { ReactComponent as DeleteGreyIcon } from "../../../myfin_v2/assets/icons/deleteGrey.svg";
import { ReactComponent as PlusPrimaryIcon } from "../../../myfin_v2/assets/icons/plusprimary.svg";

import ConsolDropdown from "../../elements/ConsolDropdown";
import {
  addConsolManualAdjustmentApiHandler,
  editConsolManualAdjustmentApiHandler,
  getConsolPopulateFSDetailsApiHandler,
  getPopulateFSFormulaOptions,
  getConsolAdjustmentOptionsApiHandler,
  getConsolAdjustmentCompaniesApiHandler,
  getConsolAdjustmentLedgersApiHandler,
  getConsolAdjustmentSocieRowsApiHandler,
  updateAutoAdjustmentMappingApiHandler,
  updateICEMappingApiHandler
} from "../../../WorkSpace/Components/ApiServices/apiHandler";


const parseAdjustmentDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === "string" && /^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [d, m, y] = dateStr.split("-");
    const parsed = dayjs(`${y}-${m}-${d}`);
    return parsed.isValid() ? parsed : null;
  }
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed : null;
};

const normalizeString = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/[-\u2012\u2013\u2014\u2015]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

const getCompanyLabel = (c) => {
  if (!c) return "";
  if (typeof c === "string") return c;
  return c.companyName || c.name || c.label || c.companyID || c._id || c.id || "";
};

const getCompanyID = (c) => {
  if (!c) return "";
  if (typeof c === "string") return c;
  return c.companyID || c._id || c.id || c.value || c.companyName || c.name || "";
};

const getLedgerLabel = (l) => {
  if (!l) return "";
  if (typeof l === "string") return l;
  return l.ledgerCode || l.ledger_code || l.code || l.ledgerName || l.name || l.label || l.ledger_name || "";
};

const getLedgerCode = (l) => {
  if (!l) return "";
  if (typeof l === "string") return l;
  return l.ledgerCode || l.ledger_code || l.code || l.ledgerID || l._id || l.id || l.value || l.ledgerName || l.name || "";
};

const getSocieRowLabel = (r) => {
  if (!r) return "";
  if (typeof r === "string") return r;
  return (
    r.label ||
    r.socieRow ||
    r.rowName ||
    r.name ||
    r.particular ||
    r.row ||
    r.title ||
    r.heading ||
    r._id ||
    r.id ||
    ""
  );
};

const getSocieRowValue = (r) => {
  if (!r) return "";
  if (typeof r === "string") return r;
  return (
    r.value ||
    r.socieRow ||
    r.rowName ||
    r.name ||
    r.particular ||
    r.label ||
    r._id ||
    r.id ||
    ""
  );
};

const getNodeLabel = (node) => {
  if (!node) return "";
  if (typeof node === "string") return node;

  return (
    node.levelOne ||
    node.levelone ||
    node.levelTwo ||
    node.levelThree ||
    node.name ||
    node.particular ||
    node.section ||
    node.subSection ||
    ""
  );
};

const findNodeObject = (node, candidates = []) => {
  if (!node) return null;
  if (typeof node === "object") return node;

  const normTarget = normalizeString(node);
  return (
    candidates.find(
      (c) => normalizeString(getNodeLabel(c)) === normTarget
    ) || null
  );
};

const getNodeChildren = (node, parentNode = null, rootOptions = []) => {
  if (!node) return [];

  let resolvedNode = node;
  if (typeof resolvedNode === "string") {
    if (parentNode) {
      const parentChildren = getNodeChildren(parentNode, null, rootOptions);
      resolvedNode = findNodeObject(node, parentChildren);
    }
    if (!resolvedNode || typeof resolvedNode === "string") {
      resolvedNode = findNodeObject(node, rootOptions);
    }
  }

  if (!resolvedNode || typeof resolvedNode === "string") return [];

  return (
    resolvedNode.subgroup ||
    resolvedNode.subGroup ||
    resolvedNode.subSections ||
    resolvedNode.rows ||
    []
  );
};

const CreateEditEntryModal = ({
  consolID,
  handleClose,
  editData,
  getManualAdjustments,
  projectID,
  type = "manual",
  getInterCompanyEliminations,
  interimReporting = false,
}) => {
  /* -------------------- BASIC STATE -------------------- */
  const [date, setDate] = useState(
    editData?.adjustmentDate ? parseAdjustmentDate(editData.adjustmentDate) : null
  );
  const [remarks, setRemarks] = useState(editData?.remarks || "");

  const [optionsData, setOptionsData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [socieRowsData, setSocieRowsData] = useState([]);
  const [successPopup, setSuccessPopup] = useState({ isOpen: false, message: "" });

  const [particulars, setParticulars] = useState([
    {
      levelone: null,
      levelTwo: null,
      levelThree: null,
      company: null,
      ledger: null,
      ledgerOptions: [],
      socieRow: null,
      currentAmount: "",
      previousAmount: "",
      comparativeAmount: "",
    },
  ]);

  const formatIndianNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";

    const num = Number(value);

    if (isNaN(num)) return value;

    const formatted = Math.abs(num).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

    return num < 0 ? `(${formatted})` : formatted;
  };

  useEffect(() => {
    if (!consolID) return;

    getConsolAdjustmentOptionsApiHandler({ consolID })
      .then((res) => {
        console.log("adjustmentOptions response", res.data);
        const resData = res?.data?.data || res?.data?.options || res?.data || [];
        
        if (resData && typeof resData === "object" && !Array.isArray(resData)) {
          const bs = resData.bs || [];
          const pl = resData.pl || [];
          if (bs.length > 0 || pl.length > 0) {
            setOptionsData([...bs, ...pl]);
          }
        } else if (Array.isArray(resData) && resData.length > 0) {
          setOptionsData(resData);
        }
      })
      .catch((err) => {
        console.error("Failed to load adjustment options:", err);
      });

    getConsolAdjustmentCompaniesApiHandler({ consolID })
      .then((res) => {
        console.log("adjustmentCompanies response", res.data);
        const resData = res?.data?.companies || res?.data?.data || res?.data?.options || res?.data || [];
        if (Array.isArray(resData)) {
          setCompaniesData(resData);
        }
      })
      .catch((err) => {
        console.error("Failed to load adjustment companies:", err);
      });

    getConsolAdjustmentSocieRowsApiHandler({ consolID })
      .then((res) => {
        console.log("adjustmentSocieRows response", res.data);
        const resData =
          res?.data?.socieRows ||
          res?.data?.data ||
          res?.data?.options ||
          res?.data?.rows ||
          res?.data ||
          [];
        if (Array.isArray(resData)) {
          setSocieRowsData(resData);
        }
      })
      .catch((err) => {
        console.error("Failed to load adjustment socie rows:", err);
      });
  }, [consolID]);

  /* -------------------- OPTIONS -------------------- */
  const level1Options = useMemo(
    () =>
      optionsData.map((item) => ({
        label: getNodeLabel(item),
        value: item,
      })),
    [optionsData]
  );

  const companyOptions = useMemo(
    () =>
      companiesData.map((item) => ({
        label: getCompanyLabel(item),
        value: item,
      })),
    [companiesData]
  );

  const socieRowOptions = useMemo(
    () =>
      socieRowsData.map((item) => ({
        label: getSocieRowLabel(item),
        value: item,
      })),
    [socieRowsData]
  );

  /* -------------------- MAP EDIT DATA → UI STATE -------------------- */
  useEffect(() => {
    if (!editData || optionsData.length === 0) return;

    const mapped = editData.adjustments.map((adj) => {
      const normLvl1 = normalizeString(adj.levelOne);
      const lvl1 =
        optionsData.find((o) => normalizeString(getNodeLabel(o)) === normLvl1) ||
        adj.levelOne ||
        null;

      const normLvl2 = normalizeString(adj.levelTwo);
      const lvl1Children = getNodeChildren(lvl1, null, optionsData);
      const lvl2 =
        lvl1Children.find(
          (s) => normalizeString(getNodeLabel(s)) === normLvl2
        ) ||
        adj.levelTwo ||
        null;

      const normLvl3 = normalizeString(adj.levelThree);
      const lvl2Children = getNodeChildren(lvl2, lvl1, optionsData);
      const lvl3 =
        lvl2Children.find(
          (s) => normalizeString(getNodeLabel(s)) === normLvl3
        ) ||
        adj.levelThree ||
        null;

      const compVal = adj.company || adj.companyName || adj.companyID || null;
      const compObj = companiesData.find((c) => getCompanyID(c) === getCompanyID(compVal) || getCompanyLabel(c) === getCompanyLabel(compVal)) || compVal;

      const legVal = adj.ledgerCode || adj.ledger_code || adj.ledger || adj.ledgerName || adj.ledgerID || null;

      const socVal = adj.socieRow || adj.socie_row || adj.socie || null;
      const socObj =
        socieRowsData.find(
          (s) =>
            getSocieRowValue(s) === getSocieRowValue(socVal) ||
            getSocieRowLabel(s) === getSocieRowLabel(socVal)
        ) || socVal;

      return {
        levelone: lvl1,
        levelTwo: lvl2,
        levelThree: lvl3,
        company: compObj,
        ledger: legVal,
        ledgerOptions: [],
        socieRow: socObj,
        currentAmount: adj.currentYear,
        previousAmount: adj.previousYear,
        comparativeAmount:
          adj.comparative !== undefined && adj.comparative !== null
            ? adj.comparative
            : "",
      };
    });

    setParticulars(mapped);

    mapped.forEach((adj, idx) => {
      const cId = getCompanyID(adj.company);
      const legVal = adj.ledger;
      if (consolID && cId) {
        getConsolAdjustmentLedgersApiHandler({ consolID, companyID: cId })
          .then((res) => {
            const resData = res?.data?.ledgers || res?.data?.data || res?.data?.options || res?.data || [];
            if (Array.isArray(resData)) {
              const matchedLedger = resData.find(
                (l) => getLedgerCode(l) === getLedgerCode(legVal) || getLedgerLabel(l) === getLedgerLabel(legVal)
              ) || legVal;
              handleRowChange(idx, {
                ledgerOptions: resData,
                ledger: matchedLedger,
              });
            }
          })
          .catch((err) => {
            console.error("Failed to load ledgers for editData:", err);
          });
      }
    });
  }, [editData, optionsData, companiesData, socieRowsData]);

  /* -------------------- ROW HANDLERS -------------------- */
  const handleRowChange = (index, updates) => {
    setParticulars((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        ...updates,
      };
      return updated;
    });
  };

  const handleChange = (index, field, value) => {
    handleRowChange(index, { [field]: value });
  };

  const getLevelsFromLedger = (l) => {
    if (!l || typeof l !== "object") return null;

    const lvl1Str =
      l.levelOne ||
      l.levelone ||
      l.level1 ||
      l.level_one ||
      l.levelOneName ||
      l.section ||
      "";
    const lvl2Str =
      l.levelTwo ||
      l.leveltwo ||
      l.level2 ||
      l.level_two ||
      l.levelTwoName ||
      l.subSection ||
      "";
    const lvl3Str =
      l.levelThree ||
      l.levelthree ||
      l.level3 ||
      l.level_three ||
      l.levelThreeName ||
      l.particular ||
      "";

    if (!lvl1Str && !lvl2Str && !lvl3Str) return null;

    return { lvl1Str, lvl2Str, lvl3Str };
  };

  const matchLevelsToOptions = (lvl1Str, lvl2Str, lvl3Str) => {
    if (!optionsData || optionsData.length === 0) {
      return {
        levelone: lvl1Str || null,
        levelTwo: lvl2Str || null,
        levelThree: lvl3Str || null,
      };
    }

    const normLvl1 = normalizeString(lvl1Str);
    const lvl1 =
      optionsData.find((o) => normalizeString(getNodeLabel(o)) === normLvl1) ||
      lvl1Str ||
      null;

    const normLvl2 = normalizeString(lvl2Str);
    const lvl1Children = getNodeChildren(lvl1, null, optionsData);
    const lvl2 =
      lvl1Children.find(
        (s) => normalizeString(getNodeLabel(s)) === normLvl2
      ) ||
      lvl2Str ||
      null;

    const normLvl3 = normalizeString(lvl3Str);
    const lvl2Children = getNodeChildren(lvl2, lvl1, optionsData);
    const lvl3 =
      lvl2Children.find(
        (s) => normalizeString(getNodeLabel(s)) === normLvl3
      ) ||
      lvl3Str ||
      null;

    return {
      levelone: lvl1,
      levelTwo: lvl2,
      levelThree: lvl3,
    };
  };

  const handleLedgerChange = (index, selectedLedger) => {
    const updates = {
      ledger: selectedLedger,
    };

    const levels = getLevelsFromLedger(selectedLedger);
    if (levels) {
      const matched = matchLevelsToOptions(
        levels.lvl1Str,
        levels.lvl2Str,
        levels.lvl3Str
      );
      if (matched.levelone) updates.levelone = matched.levelone;
      if (matched.levelTwo) updates.levelTwo = matched.levelTwo;
      if (matched.levelThree) updates.levelThree = matched.levelThree;
    }

    const socVal = selectedLedger?.socieRow || selectedLedger?.socie_row || selectedLedger?.socie || null;
    if (socVal) {
      const socObj =
        socieRowsData.find(
          (s) =>
            getSocieRowValue(s) === getSocieRowValue(socVal) ||
            getSocieRowLabel(s) === getSocieRowLabel(socVal)
        ) || socVal;
      updates.socieRow = socObj;
    }

    handleRowChange(index, updates);
  };

  const handleCompanyChange = (index, selectedCompany) => {
    const companyID = getCompanyID(selectedCompany);
    handleRowChange(index, {
      company: selectedCompany,
      ledger: null,
      ledgerOptions: [],
    });

    if (consolID && companyID) {
      getConsolAdjustmentLedgersApiHandler({ consolID, companyID })
        .then((res) => {
          const resData = res?.data?.ledgers || res?.data?.data || res?.data?.options || res?.data || [];
          const ledgerArr = Array.isArray(resData) ? resData : [];
          handleRowChange(index, {
            ledgerOptions: ledgerArr,
          });
        })
        .catch((err) => {
          console.error("Failed to load adjustment ledgers:", err);
        });
    }
  };

  const handleAddParticular = () => {
    setParticulars((prev) => [
      ...prev,
      {
        levelone: null,
        levelTwo: null,
        levelThree: null,
        company: null,
        ledger: null,
        ledgerOptions: [],
        socieRow: null,
        currentAmount: "",
        previousAmount: "",
        comparativeAmount: "",
      },
    ]);
  };

  const handleRemoveParticular = (index) => {
    setParticulars((prev) => prev.filter((_, i) => i !== index));
  };

  /* -------------------- SUBMIT -------------------- */
  const handleSubmit = () => {
    if (type === "auto") {
      const payload = {
        adjustments: particulars.map((p) => {
          const item = {
            levelOne: getNodeLabel(p.levelone),
            levelTwo: getNodeLabel(p.levelTwo),
            levelThree: getNodeLabel(p.levelThree),
          };
          const isOtherEquity =
            getNodeLabel(p.levelTwo)?.toLowerCase() === "other equity";
          if (isOtherEquity && p.socieRow) {
            item.socieRow = getSocieRowLabel(p.socieRow);
          }
          const compID = getCompanyID(p.company);
          if (compID) item.companyID = compID;
          const legCode = getLedgerCode(p.ledger);
          if (legCode) item.ledgerCode = legCode;
          return item;
        }),
      };

        updateAutoAdjustmentMappingApiHandler(
        { consolID: consolID, autoAdjustmentID: editData?.autoAdjustmentID || editData?._id },
        payload
      )
        .then((response) => {
          if (response?.data?.success === true) {
            if (getManualAdjustments) getManualAdjustments();
            setSuccessPopup({ isOpen: true, message: "Auto Adjustment updated successfully" });
            setTimeout(() => {
              setSuccessPopup({ isOpen: false, message: "" });
              handleClose();
            }, 1500);
          } else {
            swal(
              "Error",
              response?.data?.error || "Unable to update Auto Adjustment",
              "error"
            );
          }
        })
        .catch((error) => {
          if (getManualAdjustments) getManualAdjustments();
          swal(
            "Error",
            error?.response?.data?.error || "Unable to update Auto Adjustment",
            "error"
          );
        });
    } else if (type === "ice") {
      const payload = {
        adjustments: particulars.map((p) => {
          const item = {
            levelOne: getNodeLabel(p.levelone),
            levelTwo: getNodeLabel(p.levelTwo),
            levelThree: getNodeLabel(p.levelThree),
          };
          const isOtherEquity =
            getNodeLabel(p.levelTwo)?.toLowerCase() === "other equity";
          if (isOtherEquity && p.socieRow) {
            item.socieRow = getSocieRowLabel(p.socieRow);
          }
          const compID = getCompanyID(p.company);
          if (compID) item.companyID = compID;
          const legCode = getLedgerCode(p.ledger);
          if (legCode) item.ledgerCode = legCode;
          return item;
        }),
      };

      updateICEMappingApiHandler(
        { consolID: consolID, iceID: editData?._id },
        payload
      )
        .then((response) => {
          if (response?.data?.success === true) {
            if (getInterCompanyEliminations) getInterCompanyEliminations();
            setSuccessPopup({ isOpen: true, message: "ICE Adjustment updated successfully" });
            setTimeout(() => {
              setSuccessPopup({ isOpen: false, message: "" });
              handleClose();
            }, 1500);
          } else {
            swal(
              "Error",
              response?.data?.error || "Unable to update ICE Adjustment",
              "error"
            );
          }
        })
        .catch((error) => {
          if (getInterCompanyEliminations) getInterCompanyEliminations();
          swal(
            "Error",
            error?.response?.data?.error || "Unable to update ICE Adjustment",
            "error"
          );
        });
    } else {
      const payload = {
        adjustmentDate: date ? dayjs(date).format("DD-MM-YYYY") : null,
        remarks,
        adjustments: particulars.map((p) => {
          const item = {
            levelOne: getNodeLabel(p.levelone),
            levelTwo: getNodeLabel(p.levelTwo),
            levelThree: getNodeLabel(p.levelThree),
            currentYear: p.currentAmount,
            previousYear: p.previousAmount,
          };
          if (
            p.comparativeAmount !== "" &&
            p.comparativeAmount !== undefined &&
            p.comparativeAmount !== null
          ) {
            item.comparative = p.comparativeAmount;
          }
          const isOtherEquity =
            getNodeLabel(p.levelTwo)?.toLowerCase() === "other equity";
          if (isOtherEquity && p.socieRow) {
            item.socieRow = getSocieRowLabel(p.socieRow);
          }
          const compID = getCompanyID(p.company);
          if (compID) item.companyID = compID;
          const legCode = getLedgerCode(p.ledger);
          if (legCode) item.ledgerCode = legCode;
          return item;
        }),
      };

      if (editData) {
        editConsolManualAdjustmentApiHandler(
          { consolID: consolID, manualAdjustmentID: editData?._id },
          payload
        )
          .then((response) => {
            if (response?.data?.success === true) {
              if (getManualAdjustments) getManualAdjustments();
              setSuccessPopup({ isOpen: true, message: "Adjustment updated successfully" });
              setTimeout(() => {
                setSuccessPopup({ isOpen: false, message: "" });
                handleClose();
              }, 1500);
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to update Adjustment",
                "error"
              );
            }
          })
          .catch((error) => {
            if (getManualAdjustments) getManualAdjustments();
            swal(
              "Error",
              error?.response?.data?.error || "Unable to update Adjustment",
              "error"
            );
          });
      } else {
        addConsolManualAdjustmentApiHandler({ consolID: consolID }, payload)
          .then((response) => {
            if (response?.data?.success === true) {
              if (getManualAdjustments) getManualAdjustments();
              setSuccessPopup({ isOpen: true, message: "Adjustment added successfully" });
              setTimeout(() => {
                setSuccessPopup({ isOpen: false, message: "" });
                handleClose();
              }, 1500);
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to add Adjustment",
                "error"
              );
            }
          })
          .catch((error) => {
            if (getManualAdjustments) getManualAdjustments();
            swal(
              "Error",
              error?.response?.data?.error || "Unable to add Adjustment",
              "error"
            );
          });
      }
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <>
    <Modal
      width="680px"
      title={(editData || type === "auto" || type === "ice") ? "Edit Entry" : "Create Entry"}
      bodycss={commoncss.unsetPadding}
      headerActions={<CloseIcon className="clickable" onClick={handleClose} />}
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            label="Cancel"
            onClick={handleClose}
          />
          <Button
            btnColor={buttoncss.primary}
            label={(editData || type === "auto" || type === "ice") ? "Update" : "Create Entry"}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <div className={commoncss.pd1530}>
        {/* DATE */}
        <InputGroup
          label="Choose Date"
          class2_="mt-1"
          inputs={[
            <>
              <CommonDatePicker
                value={date}
                onChange={(d) => {
                  if (!editData) setDate(d);
                }}
                placeholder="DD-MMM-YYYY"
                containerClass={`${commoncss.datePickerContainer} ${!editData ? commoncss.clickable : commoncss.noActions}`}
                iconStyle={{ display: "none" }}
                inputStyle={{ height: "37px" }}
              />
              <span className="icon" style={{ marginTop: "-4px" }}>
                <CalenderIcon width={20} height={20} />
              </span>
            </>,
          ]}
        />

        {/* REMARKS */}
        <InputGroup
          label="Remarks"
          inputs={[
            <textarea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks"
              className={`${commoncss.placeholderStyle} ${editData ? commoncss.greyInput : ""}`}
              disabled={!!editData}
            />,
          ]}
        />

        {/* ADJUSTMENTS */}
        {particulars.map((item, index) => {
          const isOtherEquity =
            getNodeLabel(item.levelTwo)?.toLowerCase() === "other equity";
          console.log("Level 2 Selected:", item.levelTwo);
          console.log("Level 3 Data:", item.levelTwo?.subgroup);

          return (
            <div
              key={index}
              style={{
                border: "1px solid #EAEEED",
                padding: "16px",
                borderRadius: "10px",
                marginTop: "15px",
                position: "relative",
                zIndex: particulars.length - index,
              }}
            >
                {/* COMPANY */}
              <div style={{ position: "relative", zIndex: 7, marginBottom: "14px" }}>
                <InputGroup
                  label={
                    <div className="attachments-button-label">
                      <span>Select Company</span>
                      {particulars.length > 1 && (
                        <span
                          className="mb-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleRemoveParticular(index)}
                        >
                          <DeleteGreyIcon />
                        </span>
                      )}
                    </div>
                  }
                  inputs={[
                    <ConsolDropdown
                      options={companyOptions}
                      selected={
                        item.company
                          ? { label: getCompanyLabel(item.company), value: item.company }
                          : null
                      }
                      onSelect={(sel) => {
                        handleCompanyChange(index, sel ? sel.value : null);
                      }}
                      placeHolder="Select Company"
                      className={commoncss.placeholderStyle}
                      isEditable={true}
                    />,
                  ]}
                />
              </div>

              {/* SELECT LEDGERS */}
              <div style={{ position: "relative", zIndex: 6, marginBottom: "14px" }}>
                <InputGroup
                  label="Select Ledgers Code"
                  inputs={[
                    <ConsolDropdown
                      options={(item.ledgerOptions || []).map((l) => ({
                        label: getLedgerLabel(l),
                        value: l,
                      }))}
                      selected={
                        item.ledger
                          ? { label: getLedgerLabel(item.ledger), value: item.ledger }
                          : null
                      }
                      onSelect={(sel) =>
                        handleLedgerChange(index, sel ? sel.value : null)
                      }
                      disabled={!item.company}
                      placeHolder="Select Ledgers Code"
                      className={commoncss.placeholderStyle}
                      isEditable={true}
                    />,
                  ]}
                />
              </div>
              
              {/* LEVEL 1 */}
              <div style={{ position: "relative", zIndex: 5, marginBottom: "14px" }}>
                <InputGroup
                  label="Level 1"
                  inputs={[
                    <ConsolDropdown
                      options={level1Options}
                      selected={
                        item.levelone
                          ? { label: getNodeLabel(item.levelone), value: item.levelone }
                          : null
                      }
                      onSelect={(sel) => {
                        handleRowChange(index, {
                          levelone: sel ? sel.value : null,
                          levelTwo: null,
                          levelThree: null,
                          socieRow: null,
                        });
                      }}
                      placeHolder="Select Level 1"
                      className={commoncss.placeholderStyle}
                      isEditable={true}
                    />,
                  ]}
                />
              </div>

              {/* LEVEL 2 */}
              <div style={{ position: "relative", zIndex: 4, marginBottom: "14px" }}>
                <InputGroup
                  label="Level 2"
                  inputs={[
                    <ConsolDropdown
                      options={
                        getNodeChildren(item.levelone, null, optionsData).map((s) => ({
                          label: getNodeLabel(s),
                          value: s,
                        }))
                      }
                      selected={
                        item.levelTwo
                          ? { label: getNodeLabel(item.levelTwo), value: item.levelTwo }
                          : null
                      }
                      onSelect={(sel) => {
                        handleRowChange(index, {
                          levelTwo: sel ? sel.value : null,
                          levelThree: null,
                          socieRow: null,
                        });
                      }}
                      disabled={!item.levelone}
                      placeHolder="Select Level 2"
                      className={commoncss.placeholderStyle}
                      isEditable={true}
                    />,
                  ]}
                />
              </div>

              {/* LEVEL 3 */}
              <div style={{ position: "relative", zIndex: 3, marginBottom: "14px" }}>
                <InputGroup
                  label="Level 3"
                  inputs={[
                    <ConsolDropdown
                      options={
                        getNodeChildren(item.levelTwo, item.levelone, optionsData).map((s) => ({
                          label: getNodeLabel(s),
                          value: s,
                        }))
                      }
                      selected={
                        item.levelThree
                          ? {
                              label: getNodeLabel(item.levelThree),
                              value: item.levelThree,
                            }
                          : null
                      }
                      onSelect={(sel) =>
                        handleRowChange(index, {
                          levelThree: sel ? sel.value : null,
                        })
                      }
                      disabled={!item.levelTwo}
                      placeHolder="Select Level 3"
                      className={commoncss.placeholderStyle}
                      isEditable={true}
                    />,
                  ]}
                />
              </div>

              {/* SOCIE ROW (AFTER LEVEL 3) */}
              {isOtherEquity && (
                <div style={{ position: "relative", zIndex: 2, marginBottom: "14px" }}>
                  <InputGroup
                    label="SOCIE Row"
                    inputs={[
                      <ConsolDropdown
                        options={socieRowOptions}
                        selected={
                          item.socieRow
                            ? {
                                label: getSocieRowLabel(item.socieRow),
                                value: item.socieRow,
                              }
                            : null
                        }
                        onSelect={(sel) =>
                          handleRowChange(index, {
                            socieRow: sel ? sel.value : null,
                          })
                        }
                        placeHolder="Select Socie Row"
                        className={commoncss.placeholderStyle}
                        isEditable={true}
                      />,
                    ]}
                  />
                </div>
              )}


              <div className="row" style={{ marginTop: 0 }}>
                <div
                  className={interimReporting ? "col-md-4" : "col-md-6"}
                  style={{ paddingRight: "5px" }}
                >
                  <InputGroup
                    label="Current Year Amount"
                    inputs={[
                      <input
                        type="text"
                        placeholder="Enter Amount"
                        className={commoncss.placeholderStyle}
                        value={
                          type === "auto" || type === "ice"
                            ? formatIndianNumber(item.currentAmount)
                            : item.currentAmount
                        }
                        disabled={type === "auto" || type === "ice"}
                        onChange={(e) =>
                          handleChange(index, "currentAmount", e.target.value)
                        }
                      />,
                    ]}
                  />
                </div>
                <div
                  className={interimReporting ? "col-md-4" : "col-md-6"}
                  style={{
                    paddingLeft: "5px",
                    paddingRight: interimReporting ? "5px" : undefined,
                  }}
                >
                  <InputGroup
                    label="Previous Year Amount"
                    inputs={[
                      <input
                        type="text"
                        placeholder="Enter Amount"
                        value={
                          type === "auto" || type === "ice"
                            ? formatIndianNumber(item.previousAmount)
                            : item.previousAmount
                        }
                        disabled={type === "auto" || type === "ice"}
                        className={commoncss.placeholderStyle}
                        onChange={(e) =>
                          handleChange(index, "previousAmount", e.target.value)
                        }
                      />,
                    ]}
                  />
                </div>

                {interimReporting && (
                <div className="col-md-4" style={{ paddingLeft: "5px" }}>
                  <InputGroup
                    label="Comparative Amount"
                    inputs={[
                      <input
                        type="text"
                        placeholder="Enter Amount"
                        value={
                          type === "auto" || type === "ice"
                            ? formatIndianNumber(item.comparativeAmount)
                            : item.comparativeAmount
                        }
                        disabled={type === "auto" || type === "ice"}
                        className={commoncss.placeholderStyle}
                        onChange={(e) =>
                          handleChange(index, "comparativeAmount", e.target.value)
                        }
                      />,
                    ]}
                  />
                </div>
                )}
              </div>
            </div>
          );
        })}

        <div
          onClick={handleAddParticular}
          style={{
            color: "#2D8861",
            fontSize: "14px",
            marginTop: "10px",
            cursor: "pointer",
          }}
        >
          <PlusPrimaryIcon style={{  marginTop: "-3.5px" }}/> Add Field
        </div>
      </div>
    </Modal>
    <SuccessPopup
      isOpen={successPopup.isOpen}
      message={successPopup.message}
      onClose={() => {
        setSuccessPopup({ isOpen: false, message: "" });
        handleClose();
      }}
    />
    </>
  );
};

export default CreateEditEntryModal;
